import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {BookOpen, Calculator, TrendingUp, Users} from 'lucide-react';
import { Link } from "react-router-dom";

function Formulas() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#006747] rounded-xl mb-4">
              <BookOpen className="text-white" size={32} />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Golf Handicap Formulas & Education
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complete guide to USGA handicap formulas, calculations, and terminology. Understand the math behind your golf handicap.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          {/* Handicap Index Formula */}
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="text-[#006747]" size={28} />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Handicap Index Formula
              </h2>
            </div>
            
            <p className="text-gray-700 mb-4">
              Your <strong>handicap index</strong> represents your potential scoring ability on a course of standard difficulty. It's calculated from your score differentials.
            </p>

            <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-[#006747] mb-4">
              <div className="font-mono text-gray-800 mb-2">
                Score Differential = (Adjusted Gross Score - Course Rating) × 113 / Slope Rating
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Where 113 is the standard slope rating
              </p>
            </div>

            <div className="space-y-3 text-gray-700">
              <div className="bg-[#F0FDF4] p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Example Calculation:</h4>
                <ul className="space-y-1 text-sm">
                  <li>• Your score: 85</li>
                  <li>• Course rating: 71.5</li>
                  <li>• Slope rating: 130</li>
                  <li className="text-[#006747] font-semibold">→ Score Differential: (85 - 71.5) × 113 / 130 = 11.7</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600">
                <strong>Official Handicap Index:</strong> The average of the best 8 of your most recent 20 score differentials, multiplied by 0.96 (excellence factor).
              </p>
            </div>
          </div>

          {/* USGA Course Handicap Formula */}
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="text-[#006747]" size={28} />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                USGA Course Handicap Formula
              </h2>
            </div>

            <p className="text-gray-700 mb-4">
              Your <strong>course handicap</strong> converts your handicap index to the specific course and tees you're playing from.
            </p>

            <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-[#006747] mb-4">
              <div className="font-mono text-gray-800 mb-2">
                Course Handicap = Handicap Index × (Slope Rating / 113) + (Course Rating - Par)
              </div>
            </div>

            <div className="space-y-3 text-gray-700">
              <div className="bg-[#F0FDF4] p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Example Calculation:</h4>
                <ul className="space-y-1 text-sm">
                  <li>• Handicap index: 12.4</li>
                  <li>• Course slope: 125</li>
                  <li>• Course rating: 70.8</li>
                  <li>• Par: 72</li>
                  <li className="text-[#006747] font-semibold">→ Course Handicap: 12.4 × (125/113) + (70.8-72) = 12.5 ≈ 13</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600">
                <strong>Note:</strong> Course handicaps are rounded to the nearest whole number. The second part of the formula adjusts for courses that are easier or harder than their par suggests.
              </p>
            </div>
          </div>

          {/* Scramble Formulas */}
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <Users className="text-[#006747]" size={28} />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Scramble Handicap Formulas
              </h2>
            </div>

            <p className="text-gray-700 mb-4">
              Team handicaps for scramble tournaments use percentage-based formulas to maintain fairness.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                <h4 className="font-semibold text-[#006747] mb-2">2-Man Scramble</h4>
                <div className="font-mono text-sm text-gray-800 mb-2">
                  Team = (35% × Lower) + (15% × Higher)
                </div>
                <p className="text-xs text-gray-600">Total: 50% of combined handicaps</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                <h4 className="font-semibold text-[#006747] mb-2">4-Man Scramble</h4>
                <div className="font-mono text-sm text-gray-800 mb-2">
                  Team = 20%A + 15%B + 10%C + 5%D
                </div>
                <p className="text-xs text-gray-600">A-D sorted from lowest to highest handicap</p>
              </div>
            </div>

            <Link
              to="/scramble-tools"
              className="inline-flex items-center gap-2 text-[#006747] font-medium hover:underline"
            >
              Try the Scramble Calculator →
            </Link>
          </div>

          {/* Glossary Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
              Handicap Glossary
            </h2>

            <div className="space-y-5">
              <div className="border-l-4 border-[#006747] pl-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Handicap Index
                </h3>
                <p className="text-gray-700">
                  A portable number that represents a player's demonstrated ability. It's calculated using the best 8 of your most recent 20 scores and can be used at any course.
                </p>
              </div>

              <div className="border-l-4 border-[#006747] pl-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Course Rating
                </h3>
                <p className="text-gray-700">
                  The expected score for a scratch golfer (0.0 handicap) on a specific set of tees. Typically close to par but accounts for actual course difficulty.
                </p>
              </div>

              <div className="border-l-4 border-[#006747] pl-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Slope Rating
                </h3>
                <p className="text-gray-700">
                  A measure of the relative difficulty of a course for bogey golfers compared to scratch golfers. Ranges from 55 to 155, with 113 being standard/average difficulty.
                </p>
              </div>

              <div className="border-l-4 border-[#006747] pl-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Score Differential
                </h3>
                <p className="text-gray-700">
                  The numerical value of a score in relation to the difficulty of the course. Used to calculate your handicap index - represents how well you played relative to course difficulty.
                </p>
              </div>

              <div className="border-l-4 border-[#006747] pl-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Course Handicap
                </h3>
                <p className="text-gray-700">
                  The number of strokes a player receives for a specific course and tee. This is the handicap used when playing a round and posting a score.
                </p>
              </div>

              <div className="border-l-4 border-[#006747] pl-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Handicap Index Conversion
                </h3>
                <p className="text-gray-700">
                  The process of converting your handicap index into a course handicap using the course's slope rating and the difference between course rating and par.
                </p>
              </div>

              <div className="border-l-4 border-[#006747] pl-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  GHIN (Golf Handicap and Information Network)
                </h3>
                <p className="text-gray-700">
                  The official handicap service of the USGA. Provides a centralized system for calculating and maintaining official golf handicaps in the United States.
                </p>
              </div>

              <div className="border-l-4 border-[#006747] pl-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Adjusted Gross Score
                </h3>
                <p className="text-gray-700">
                  Your actual score after applying ESC (Equitable Stroke Control) - a maximum score limit per hole based on your course handicap. Prevents one bad hole from inflating your handicap.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-[#006747] to-[#005239] rounded-xl shadow-lg p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Calculate Your Handicap?
            </h3>
            <p className="text-lg mb-6 text-gray-100">
              Use our free USGA-aligned calculators to find your handicap index, course handicap, or team scramble handicap.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-white text-[#006747] py-3 px-8 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Handicap Calculator
              </Link>
              <Link
                to="/usga-calculator"
                className="bg-transparent border-2 border-white text-white py-3 px-8 rounded-lg font-semibold hover:bg-white hover:text-[#006747] transition-colors"
              >
                Course Handicap
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Formulas;
